import React, { useEffect, useState } from 'react';
import * as z from 'zod';
import {
  ArrowRight, Backpack, BadgeCheck, LucideSkipBack, LucideStepBack, SendToBackIcon,
  SkipBack,
  StepBack,
  StepForward,
  Undo2
} from 'lucide-react';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { format, set } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { toast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@/lib/utils';
import { useUserInfo } from "@/hooks/useUserInfo.ts";
import { getSummary, sendTechnicalSupportRequest } from "@/utils/pasUtils.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import 'react-time-picker/dist/TimePicker.css'; // Import default styles
import 'react-clock/dist/Clock.css'; // Required for react-time-picker
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './ReactQuillRTL.css';

const formSchema = z.object({
  employee: z.string({
    required_error: 'الموظف is required.'
  }),
  support_order_type: z.string({
    required_error: 'نوع أمر الدعم is required.'
  }),
  support_type: z.string({
    required_error: 'نوع الدعم is required.'
  }),
  subject: z.string({
    required_error: ' الموضوع is required.'
  }).min(1, " الموضوع مطلوب"),
  details: z.string({
    required_error: 'التفاصيل is required.'
  }),
});


const ServiceTechnicalSupport = () => {
  const VITE_USERNAMET = import.meta.env.VITE_USERNAMET
  const VITE_PASSWORD = import.meta.env.VITE_PASSWORD

  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);
  const [showPrintView, setShowPrintView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState({
    profile: null,
    loading: true,
    error: null

  });
  const { greeting, fullName, email, role, profilePictureUrl } = useUserInfo();
  const [selectedFromDate, setSelectedFromDate] = useState<Date | null>(null);
  const [selectedToDate, setSelectedToDate] = useState<Date | null>(null);
  const [selectedStartTime, setSelectedStartTime] = useState('10:00');
  const [selectedEndTime, setSelectedEndTime] = useState('11:00');
  const [totalHours, setTotalHours] = useState('');
  const [support_order_types_list, setsupport_order_types_list] = useState([''])
  const [support_types_list, setsupport_types_list] = useState([''])
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      employee: data.profile ? `${data.profile.name}` : '',
      support_order_type: '',
      support_type: '',
      subject: '',
      details: '',
    },
  });
  useEffect(() => {
    async function fetchAttendance() {
      try {
        setLoading(true);

        if (email) {
          // Fetch summary data based on email    
          const summaryResponse = await getSummary(
            VITE_USERNAMET,
            VITE_PASSWORD,
            email
          );
          console.log("Profile data:", summaryResponse.profile);
          if (data.loading) {
            setData((prev) => ({
              ...prev,
              profile: summaryResponse.profile,
              loading: false,
              error: null,
            }));
            console.log("Profile data:", data);
            form.reset({
              employee: `${summaryResponse.profile.name}`,
              support_order_type: '',
              support_type: '',
              subject: '',
              details: '',
            });
          }
        }
      } catch (err) {
        setError(err.message || "Failed to fetch attendance");
        console.log(err.message || "Failed to fetch attendance");
      } finally {
        setLoading(false);
        console.log("Profile data:", data.profile);
      }
    }
    async function initialsupport_order_types_list() {
      setsupport_order_types_list(['Request', 'Problem']);
    }
    async function initialsupport_types_list() {
      setsupport_types_list(['Licence', 'Application Support', 'Device Support', 'Network Support', 'SharePoint Support',
        'Printer Support', 'Outbound Email', 'Create Close Email', 'PAS System', 'Bitbucket', 'One Drive', 'Other']);
    }

    fetchAttendance();
    initialsupport_order_types_list();
    initialsupport_types_list();
  }, [VITE_PASSWORD, VITE_USERNAMET, data, email, form, totalHours]);


  async function onSubmit(values: z.infer<typeof formSchema>) {
    console.log(values);
    toast({
      title: 'تم إرسال الطلب بنجاح',
      description: 'سيتم معالجة الطلب من قبل المسؤولين في النظام حسب التسلسل.',
    });
    try {
      const response = await sendTechnicalSupportRequest(VITE_USERNAMET,
        VITE_PASSWORD,
        {
          employee: form.getValues('employee'),
          support_order_type: form.getValues('support_order_type'),
          support_type: form.getValues('support_type'),
          subject: form.getValues('subject'),
          details: form.getValues('details'),
        });
      toast({
        title: 'تم إرسال الطلب  بنجاح',
        description: 'سيتم مراجعة طلبك من قبل الإدارة.',
        duration: 5000,
      });
      navigate('/services');
    } catch (error) {
      console.error("Request failed:", error);
      toast({
        title: 'فشل في إرسال الطلب ',
        description: error.message || 'حدث خطأ أثناء معالجة طلبك. يرجى المحاولة مرة أخرى.',
        duration: 5000,
        variant: 'destructive',
      });
    }
  }

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'align': [] }],
      ['link', 'image', 'video', 'formula'],
      ['clean']
    ],
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'blockquote', 
    'list', 'bullet', 'indent',
    'direction', 'align',
  ];


  return (
    <>
      {data.loading ? (
        <div>Loading...</div>
      ) : data.profile ? (
        <div className="w-full max-w-4xl">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/services')}
              className="gap-2"
            >
              <ArrowRight className="h-4 w-4" />
              العودة للخدمات
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-right">{t('services-technical-support.title')}</CardTitle>
              <CardDescription className="text-right">
                يرجى ملء جميع البيانات المطلوبة لتقديم {t('services-technical-support.title')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


                    {data.profile.name && <FormField
                      control={form.control}
                      name="employee"
                      rules={{
                        required: "الموظف is required." // Custom error message
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">الموظف</FormLabel>
                          <FormControl>
                            <Input
                              readOnly={true}
                              placeholder="الموظف..."
                              className="text-right"
                              {...field}
                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />}
                    <FormField
                      control={form.control}
                      name="support_order_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">نوع أمر الدعم</FormLabel>
                          <Select onValueChange={(value) => {
                            field.onChange(value);
                          }}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full p-2 border border-gray-300 rounded-md text-right flex justify-between flex-row-reverse">
                                <SelectValue placeholder="اختر نوع أمر الدعم" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {support_order_types_list && support_order_types_list.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="support_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">نوع الدعم</FormLabel>
                          <Select onValueChange={(value) => {
                            field.onChange(value);
                          }}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full p-2 border border-gray-300 rounded-md text-right flex justify-between flex-row-reverse">
                                <SelectValue placeholder="اختر نوع الدعم" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {support_types_list && support_types_list.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="subject"
                      rules={{
                        required: "الموضوع مطلوب",
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">الموضوع</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="الموضوع..."
                              className="text-right"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    

                  </div>
                  <FormField
                      control={form.control}
                      name="details"
                      rules={{
                        required: "التفاصيل is required."
                      }}

                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">التفاصيل</FormLabel>
                          <FormControl>
                            {/* <Input
                              placeholder="التفاصيل..."
                              className="text-right"
                              {...field}
                            /> */}
                            <ReactQuill
                              theme="snow"
                              // value={value}
                              // onChange={setValue}
                              modules={modules}
                              formats={formats}
                              placeholder="Start writing..."
                              
                              style={{
                                minHeight: '100%',
                                width: '100%',
                                direction: 'rtl',
                                textAlign: 'right'
                              }}
                              className="rtl-quill"
                              {...field}
                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />

                  <div className="flex gap-4 justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/services')}
                    >
                      إلغاء
                    </Button>
                    {/* <Button type="submit" disabled={isSaved}>
                  حفظ
                </Button> */}
                    <Button type="submit"
                    // onClick={handlePrint}
                    >
                      إرسال الطلب
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div >
      ) : (
        <div>No profile data available</div>
      )}
    </>
  );
};

export default ServiceTechnicalSupport;